/**
 * iter280 — Prise de plusieurs photos à la suite, regroupées en un seul PDF.
 * L'appareil photo du téléphone ne renvoie qu'un cliché à la fois : on
 * accumule donc les prises ici, puis on envoie le lot au parent.
 */
import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, FileText, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { photoCompress } from "@/components/or/photoCompress";

export default function OrPhotoCaptureDialog({ open, onClose, onDone, busy = false }) {
  const [pages, setPages] = useState([]);
  const [ajout, setAjout] = useState(false);
  const inputRef = useRef(null);

  const ajouter = async (files) => {
    const liste = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!liste.length) return;
    if (pages.length + liste.length > 30) { toast.error("30 pages maximum"); return; }
    setAjout(true);
    try {
      const nouvelles = [];
      for (const f of liste) nouvelles.push(await photoCompress(f, 600));
      setPages((p) => [...p, ...nouvelles]);
    } catch {
      toast.error("Photo illisible");
    } finally {
      setAjout(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const fermer = () => { setPages([]); onClose(); };

  const valider = async () => {
    await onDone(pages);
    setPages([]);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && fermer()}>
      <DialogContent className="max-w-lg" data-testid="or-capture-dialog">
        <DialogHeader>
          <DialogTitle>Prendre des photos</DialogTitle>
          <DialogDescription>
            Photographiez page après page : tout sera regroupé dans un seul PDF.
          </DialogDescription>
        </DialogHeader>

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="hidden"
          onChange={(e) => ajouter(e.target.files)}
          data-testid="or-capture-input"
        />

        <Button
          onClick={() => inputRef.current?.click()}
          disabled={ajout || busy}
          className="h-11 bg-[#52a675] hover:bg-[#3d8a5c]"
          data-testid="or-capture-shoot"
        >
          {ajout ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
          {pages.length ? "Photographier la page suivante" : "Prendre une photo"}
        </Button>

        {pages.length > 0 && (
          <div className="grid grid-cols-4 gap-2" data-testid="or-capture-pages">
            {pages.map((src, i) => (
              <div key={i} className="relative rounded-lg overflow-hidden border border-gray-200">
                <img src={src} alt={`Page ${i + 1}`} className="w-full h-20 object-cover" />
                <span className="absolute bottom-1 left-1 bg-white/90 text-[10px] font-semibold px-1 rounded">{i + 1}</span>
                <button
                  onClick={() => setPages(pages.filter((_, j) => j !== i))}
                  className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 hover:bg-white"
                  data-testid={`or-capture-remove-${i}`}
                >
                  <X className="w-3 h-3 text-gray-700" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-gray-500" data-testid="or-capture-count">
            {pages.length ? `${pages.length} page${pages.length > 1 ? "s" : ""}` : "Aucune page"}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={fermer} data-testid="or-capture-cancel">Annuler</Button>
            <Button
              onClick={valider}
              disabled={!pages.length || busy || ajout}
              className="bg-[#52a675] hover:bg-[#3d8a5c]"
              data-testid="or-capture-done"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Créer le PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
